'use client';

import { useLocale } from 'next-intl';
import { useMemo } from 'react';
import { useStore } from 'zustand';
import { getBookProStore } from './useBookProStore';

/**
 * Locale-aware hook to access the book pro store.
 * Automatically uses the current locale from next-intl context.
 *
 * Usage:
 * ```js
 * // Get entire store state
 * const store = useBookProStore();
 *
 * // Get specific values with selector
 * const currentStep = useBookProStore((state) => state.currentStep);
 *
 * // Get multiple values
 * const { currentStep, formData } = useBookProStore((state) => ({
 *   currentStep: state.currentStep,
 *   formData: state.formData
 * }));
 * ```
 *
 * @param {Function} selector - Optional selector function to pick specific state
 * @returns {any} The selected state or entire store state
 */
export function useBookProStore(selector) {
  const locale = useLocale();
  const store = useMemo(() => getBookProStore(locale), [locale]);

  return useStore(store, selector);
}

/**
 * Hook to get the raw Zustand store instance for the current locale.
 * Useful when you need direct store access (e.g., for getState() outside React).
 *
 * @returns {Object} The Zustand store instance
 */
export function useBookProStoreInstance() {
  const locale = useLocale();
  return useMemo(() => getBookProStore(locale), [locale]);
}

/**
 * Get the current locale from the store's state.
 * Useful for debugging or when you need locale info from the store.
 *
 * @returns {string} The locale code (e.g., 'en-us')
 */
export function useStoreLocale() {
  return useBookProStore((state) => state.locale);
}

// Re-export getBookProStore for non-React usage
export { getBookProStore };
